import { useCallback, useEffect, useImperativeHandle } from "react"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { useIsFirstRender } from "@/hooks/useIsFirstRender"
import { ChConcludeBillingSection } from "@/modules/common/billing/components/billing-form/ch/conclude/ch-conclude-billing-section"
import { DeBillingSection } from "@/modules/common/billing/components/billing-form/de/de-billing-section"
import { AdministrativeProgressNoteCard } from "@/modules/patients/detail/conclude/administrative-progress-note-card"
import { ProgressNoteCard } from "@/modules/patients/detail/conclude/progress-note-card"
import { useDocumentGeneration } from "@/modules/patients/detail/conclude/services/useDocumentGeneration"
import { CarePlanViewer } from "@/modules/patients/detail/progress-note/documents/care-plan-summary-document-preview"
import { checkFileRegeneration } from "@/modules/patients/detail/progress-note/documents/check-file-regeneration"
import { NoDocument } from "@/modules/patients/detail/progress-note/documents/no-document"
import { ProgressNoteDocument } from "@/modules/patients/detail/progress-note/types/document"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import { getPreviewProgressNotesByAppointmentId } from "@/modules/patients/detail/store/useDocumentStore"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useContextAppointment } from "@/modules/patients/hooks/useContextAppointment"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { CARE_PLAN_SUMMARY, CH_DOCUMENTS } from "@/modules/patients/types/ProgressNoteDocument"

export interface ConcludeAsideRef {
  handleDocumentSelection: (document: ProgressNoteDocument) => void
}

interface ConcludeAsideProps {
  onRegenerationSuccess: () => void
  ref?: React.Ref<ConcludeAsideRef>
}

export const ConcludeAside = ({ onRegenerationSuccess, ref }: ConcludeAsideProps) => {
  const setSelectedDocument = useConcludeDialogStoreContext((state) => state.actions.setSelectedDocument)
  const generateDocumentFile = useDocumentGeneration()
  const isFirstRender = useIsFirstRender()
  const appointmentId = useAppointmentParam()
  const countryCode = useCountryParam()
  const contextAppointment = useContextAppointment()
  const isAdministrativeRequest = contextAppointment?.service === "ADMINISTRATIVE_REQUEST"
  const previewProgressNotesInContextOfAppointment = getPreviewProgressNotesByAppointmentId(appointmentId)

  const handleDocumentSelection = useCallback(
    ({ fileUploadRequest, medgateConnectOrder, ...progressNoteDocument }: ProgressNoteDocument) => {
      if (progressNoteDocument?.renderFn) {
        setSelectedDocument({ ...progressNoteDocument, fileUploadRequest, medgateConnectOrder })
        return
      }

      const regeneration = checkFileRegeneration(progressNoteDocument, fileUploadRequest, medgateConnectOrder)

      // If regeneration needed, clear stale fields to prevent showing old document
      if (regeneration.needsRegeneration) {
        setSelectedDocument({
          ...progressNoteDocument,
          fileUploadRequest,
          medgateConnectOrder,
          fileId: null,
          signedAt: null,
          signedBy: null,
        })

        const { type, id, progressNoteId } = progressNoteDocument

        generateDocumentFile({
          variables: {
            type,
            id,
            progressNoteId,
            uploadSessionCode: regeneration.uploadSessionCode,
            medgateConnectOrderCode: regeneration.medgateConnectOrderCode,
          },
          onSuccess: (fileId) => {
            onRegenerationSuccess?.()
            setSelectedDocument({
              id,
              type,
              progressNoteId,
              fileId,
            })
          },
        })
      } else {
        setSelectedDocument({ ...progressNoteDocument, fileUploadRequest, medgateConnectOrder })
      }
    },
    [generateDocumentFile, setSelectedDocument, onRegenerationSuccess],
  )

  useImperativeHandle(
    ref,
    () => ({
      handleDocumentSelection,
    }),
    [handleDocumentSelection],
  )

  useEffect(() => {
    if (isFirstRender && previewProgressNotesInContextOfAppointment.length) {
      const progressNoteData = previewProgressNotesInContextOfAppointment[0].episodeOfCareProgressNoteData
      const carePlanSummaryNode = progressNoteData.carePlanSummary?.edges?.[0]?.node
      if (carePlanSummaryNode) {
        handleDocumentSelection({
          ...carePlanSummaryNode,
          type: CARE_PLAN_SUMMARY,
          progressNoteId: progressNoteData.id,
          renderFn: () => (
            <CarePlanViewer
              id={carePlanSummaryNode.id}
              progressNoteId={progressNoteData.id}
              fileId={carePlanSummaryNode.fileId}
              signedAt={carePlanSummaryNode.signedAt}
            />
          ),
        })
      } else {
        handleDocumentSelection({
          id: "",
          type: null,
          progressNoteId: "",
          renderFn: () => <NoDocument />,
        })
      }

      if (countryCode === "CH") {
        previewProgressNotesInContextOfAppointment.forEach((item) => {
          const progressNote = item.episodeOfCareProgressNoteData
          if (progressNote.__typename === "ch_ProgressNote" && progressNote.ch_consultationReport?.id) {
            const { id, progressNoteId } = progressNote.ch_consultationReport
            generateDocumentFile({
              variables: {
                id,
                type: CH_DOCUMENTS.CONSULTATION_REPORT,
                progressNoteId,
              },
              onSuccess: () => {
                onRegenerationSuccess()
              },
            })
          }
        })
      }
    }
  }, [
    countryCode,
    generateDocumentFile,
    handleDocumentSelection,
    isFirstRender,
    onRegenerationSuccess,
    previewProgressNotesInContextOfAppointment,
  ])

  return (
    <ScrollWrapper className="px-1 py-4">
      <div className="grid h-full gap-4" data-testid="care-plan-preview-dialog-left-side">
        {countryCode === "CH" ? (
          <ChConcludeBillingSection prefillGestationWeek isAdministrativeRequest={isAdministrativeRequest} />
        ) : (
          <DeBillingSection isAdministrativeRequest={isAdministrativeRequest} />
        )}
        {previewProgressNotesInContextOfAppointment.map((item) => {
          const { kind } = item.episodeOfCareProgressNoteData

          return kind === "ADMINISTRATIVE" ? (
            <AdministrativeProgressNoteCard key={item.episodeOfCareProgressNoteData.id} progressNoteData={item} />
          ) : (
            <ProgressNoteCard
              key={item.episodeOfCareProgressNoteData.id}
              progressNoteData={item}
              onSelect={handleDocumentSelection}
            />
          )
        })}
      </div>
    </ScrollWrapper>
  )
}
